import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, X, Info } from "lucide-react";
import Modal from "components/common/Modal";
import Combobox from "../common/Combobox";
import Toggle from "../common/Toggle";

// Sport values are sent to the API (lowercased), so they stay literal
const SPORT_SUGGESTIONS = [
  "Rugby",
  "Soccer",
  "Basketball",
  "Sailing",
  "Hurling",
  "Camogie",
  "Gaelic Football",
  "Hockey",
];

const EditTeamModal = ({ show, onClose, onSave, team }) => {
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState("");
  const [sportValue, setSportValue] = useState("");
  const [under16, setUnder16] = useState(false);

  useEffect(() => {
    if (show && team) {
      setTeamName(team.name || "");
      setSportValue(team.sport || "");
      setUnder16(!!team.under16);
    }
  }, [show, team]);

  const handleClose = () => {
    setTeamName(team?.name || "");
    setSportValue(team?.sport || "");
    setUnder16(!!team?.under16);
    onClose();
  };

  const handleSave = () => {
    if (!teamName.trim()) return;
    onSave?.({
      name: teamName.trim(),
      sport: sportValue.trim().toLowerCase(),
      under_16: under16,
    });
    handleClose();
  };

  const isValid = teamName.trim().length > 0;
  const hasChanged =
    teamName.trim() !== (team?.name || "").trim() ||
    sportValue.trim().toLowerCase() !==
      (team?.sport || "").trim().toLowerCase() ||
    under16 !== !!team?.under16;

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <Pencil size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.editTeamModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.editTeamModal.subtitle")}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Team Name Field */}
        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.addTeamModal.nameLabel")}
          </label>
          <input
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder={t("workspace.editTeamModal.namePlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Sport Selector */}
        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.addTeamModal.sportLabel")}
          </label>
          <Combobox
            suggestions={SPORT_SUGGESTIONS}
            value={sportValue}
            onChange={setSportValue}
            placeholder={t("workspace.addTeamModal.sportPlaceholder")}
          />
          <p className="mt-1.5 text-xs text-[#9CA3AF]">
            {t("workspace.addTeamModal.sportHint")}
          </p>
        </div>

        {/* Under-16 */}
        <div className="mb-7">
          <Toggle
            label={t("workspace.addTeamModal.under16Label")}
            hint={t("workspace.addTeamModal.under16Hint")}
            checked={under16}
            onChange={setUnder16}
          />
        </div>

        {/* Info Box */}
        <div
          className="flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
          <p className="text-[13px] leading-relaxed text-[#6B7280]">
            {t("workspace.editTeamModal.info")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleSave}
          disabled={!isValid || !hasChanged}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isValid && hasChanged
              ? {
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {t("workspace.shared.saveChanges")}
        </button>
      </div>
    </Modal>
  );
};

export default EditTeamModal;
